import { supabase } from '../supabaseClient';

// Power Automate Flow URL for Vacation Information
const VACATION_INFO_FLOW_URL = "https://default8861fe80ce3945c9a7a4a1ae964a13.8f.environment.api.powerplatform.com:443/powerautomate/automations/direct/workflows/95f7b54113d9455f88d2ae438a3ad8a2/triggers/manual/paths/invoke?api-version=1&sp=%2Ftriggers%2Fmanual%2Frun&sv=1.0&sig=y3MyIQ4NfTJHEUGzmO76i1KR0rwu87I9uhhrtuH_l3M";

// Power Automate Flow URL for Vacation Request Notification
const VACATION_REQUEST_FLOW_URL = "https://default8861fe80ce3945c9a7a4a1ae964a13.8f.environment.api.powerplatform.com:443/powerautomate/automations/direct/workflows/c6fa6fe17a05454fa334ef0ca9f7a542/triggers/manual/paths/invoke?api-version=1&sp=%2Ftriggers%2Fmanual%2Frun&sv=1.0&sig=5oRkUtRfStHDUul1rXroZsrdw_X7BZWv2m-otQlfmu8";

// Power Automate Flow URL for Vacation Accepted/Declined Notifications
const VACATION_ACTION_FLOW_URL = "https://default8861fe80ce3945c9a7a4a1ae964a13.8f.environment.api.powerplatform.com:443/powerautomate/automations/direct/workflows/f7057a030b534d51aa560a436c0db0ab/triggers/manual/paths/invoke?api-version=1&sp=%2Ftriggers%2Fmanual%2Frun&sv=1.0&sig=fexNHgyihEEan0TKyBerM32C3qWtFEJQHAbQiaSU3Nw";

/**
 * Triggers a Power Automate flow to send vacation information to the employee
 * @param {Object} employee - The employee object with vacation details
 */
export const sendVacationInfoEmail = async (employee) => {
  // Format date for payload
  const formatDate = (dateString) => {
    if (!dateString) return null;
    const date = new Date(dateString);
    // Add one day to compensate for timezone
    date.setDate(date.getDate() + 1);
    return date.toLocaleDateString('es-MX');
  };

  try {
    // Fetch overtime information from the overtime_vacation_days table
    let overtimeDays = 0;
    let overtimeExpiration = null;

    const { data: overtimeData, error } = await supabase
      .from('overtime_vacation_days')
      .select('days_remaining, expiration_date')
      .eq('employee_id', employee.id)
      .gt('days_remaining', 0) // Only get records with days remaining > 0
      .single();

    if (!error && overtimeData) {
      overtimeDays = overtimeData.days_remaining;
      overtimeExpiration = formatDate(overtimeData.expiration_date);
    } else if (error && error.code !== 'PGRST116') {
      console.warn('Error fetching overtime data:', error);
    }

    // Prepare payload for Power Automate
    const payload = {
      email: employee.wsp_email,
      full_name: employee.full_name,
      remaining_days: employee.remaining_days,
      days_taken: employee.days_taken || 0,
      renovation_date: formatDate(employee.renovation_day) || 'N/A',
      overtime_days: overtimeDays,
      overtime_expiration: overtimeExpiration || 'N/A'
    };

    // Send HTTP Request to Power Automate
    const response = await fetch(VACATION_INFO_FLOW_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(payload)
    });

    if (!response.ok) {
      throw new Error(`Power Automate request failed with status ${response.status}`);
    }

    // Success notification
    alert(`Se ha enviado la solicitud de correo para ${employee.full_name} correctamente.`);

  } catch (error) {
    console.error('Error sending vacation info email:', error);
    alert('Hubo un error al intentar enviar el correo. Por favor intente de nuevo más tarde.');
  }
};

/**
 * Opens the user's default email client with login credentials
 * @param {Object} userCredentials - Object containing email and password
 */
export const sendCredentialsEmail = (userCredentials) => {
  const { email, password } = userCredentials;
  
  // Create email subject and body
  const subject = 'Tus credenciales para el portal de vacaciones';
  const body = `Saludos, este es un mensaje auto generado. WSP International ahora cuenta con un portal de solicitud y manejo de vacaciones.

Puedes acceder a este portal con el URL: https://vacaciones.wsp.com.mx

Te compartimos las credenciales que necesitarás para poder ingresar a consultar y solicitar tus vacaciones cuando lo necesites: 

Usuario: ${email}
Contraseña: ${password}

Recuerda que esta contraseña es provisional, cámbiala cuando entres por primera vez al portal, para esto, simplemente dirígete a la barra de navegación en la parte superior de la pantalla, y da clic donde dice "Bienvenido".

Finalmente, ten en cuenta que este sistema es nuevo y no estarán disponibles todos los datos de años pasados. Sin embargo, si tienes dudas con respecto a tu información, cantidad de días, etc., o crees que hay un dato erróneo, no dudes en ponerte en contacto con RH.

Saludos,
Equipo de RH`;

  // Open default email client with pre-filled information
  window.location.href = `mailto:${email}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
};

/**
 * Triggers a Power Automate flow to send vacation request notification
 * @param {Array} recipientEmails - Array of recipient email addresses
 * @param {Object} employee - The employee requesting vacation
 * @param {Object} request - The vacation request details
 */
export const sendVacationRequestNotification = async (recipientEmails, employee, request) => {
  if (!recipientEmails || recipientEmails.length === 0) {
    console.error('No recipient emails provided');
    return false;
  }
  
  console.log('sendVacationRequestNotification received request:', request);
  
  // Join with semicolons
  const toEmails = (recipientEmails || [])
    .map(e => (e || '').trim())
    .filter(Boolean)
    .join(';');
  
  // Calculate total days - ensure we're getting the correct value
  const totalDays = request.is_half_day ? 0.5 : (request.total_days || 1);
  
  console.log('Total days for email:', totalDays);
  
  // Format date range nicely
  const formatDateRange = () => {
    // For half-day request, just show that single date
    if (request.is_half_day) {
      if (!request.start_date) return 'fecha no especificada';
      const date = new Date(request.start_date);
      // Adjust for timezone by adding one day
      date.setDate(date.getDate() + 1);
      return `${date.toLocaleDateString('es-MX')} (medio día)`;
    }
    
    // For full-day requests, check if we have individual dates
    if (request.vacation_dates && Array.isArray(request.vacation_dates) && request.vacation_dates.length > 0) {
      // Format each date individually with timezone adjustment
      const formattedDates = request.vacation_dates.map(dateStr => {
        const date = new Date(dateStr);
        // Adjust for timezone by adding one day
        date.setDate(date.getDate() + 1);
        return date.toLocaleDateString('es-MX');
      }).join(', ');
      return formattedDates;
    }
    
    // Fallback: If we don't have individual dates but have start/end dates
    if (request.start_date) {
      const startDate = new Date(request.start_date);
      // Adjust for timezone
      startDate.setDate(startDate.getDate() + 1);
      
      // If we have both start and end date and they're different
      if (request.end_date && request.start_date !== request.end_date) {
        const endDate = new Date(request.end_date);
        // Adjust for timezone
        endDate.setDate(endDate.getDate() + 1);
        return `${startDate.toLocaleDateString('es-MX')} hasta ${endDate.toLocaleDateString('es-MX')} (días no consecutivos)`;
      }
      
      // Just one date
      return startDate.toLocaleDateString('es-MX');
    }
    
    // If we have nothing, return a default
    return 'Días seleccionados individualmente';
  };
  
  try {
    const payload = {
      recipients: toEmails,
      employee_name: employee.full_name,
      request_dates: formatDateRange(),
      total_days: totalDays,
      request_link: "https://vacaciones.wsp.com.mx"
    };

    // Send HTTP Request to Power Automate
    const response = await fetch(VACATION_REQUEST_FLOW_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(payload)
    });

    if (!response.ok) {
      throw new Error(`Power Automate request failed with status ${response.status}`);
    }

    // Return true on success
    alert(`Se ha enviado el correo de notificación correctamente a: ${toEmails}`);
    return true;

  } catch (error) {
    console.error('Error sending vacation request notification:', error);
    alert('Hubo un error al intentar enviar el correo. Por favor intente de nuevo más tarde o contacte a RH.');
    return false;
  }
};

/**
 * Triggers a Power Automate flow to send vacation accepted/declined notification
 * @param {Object} data - Object containing recipients, employee name, days, dates, approver email, and status (accepted/declined)
 */
export const sendVacationActionNotification = async (data) => {
  const { recipients, employeeName, vacationDays, specificDates, approverEmail, status } = data;
  
  if (!recipients || (Array.isArray(recipients) && recipients.length === 0) && !recipients) {
    console.error('No recipient emails provided');
    return false;
  }
  
  // Handle recipients (can be array or single string)
  let toEmails = '';
  if (Array.isArray(recipients)) {
    toEmails = recipients.join(';');
  } else {
    toEmails = recipients;
  }
  
  // Format specific dates if it's an array
  let formattedDatesStr = '';
  if (Array.isArray(specificDates)) {
    formattedDatesStr = specificDates.join(', ');
  } else if (specificDates) {
    formattedDatesStr = specificDates;
  } else {
      formattedDatesStr = 'N/A';
  }

  try {
    const payload = {
      recipients: toEmails,
      status: status, // 'accepted' or 'declined'
      employee_name: employeeName,
      vacation_days: vacationDays || 0,
      specific_dates: formattedDatesStr,
      approver_email: approverEmail || 'N/A'
    };

    // Send HTTP Request to Power Automate
    const response = await fetch(VACATION_ACTION_FLOW_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(payload)
    });

    if (!response.ok) {
      throw new Error(`Power Automate request failed with status ${response.status}`);
    }

    alert('Se ha enviado la notificación por correo correctamente.');
    return true;

  } catch (error) {
    console.error('Error sending vacation action notification:', error);
    alert('Hubo un error al intentar enviar el correo. Por favor intente de nuevo más tarde.');
    return false;
  }
};

/**
 * Wrapper for backward compatibility - calls sendVacationActionNotification
 * @param {Object} data - Object containing recipients, employee name, days, dates and approver email
 */
export const sendVacationAcceptedEmail = (data) => {
  return sendVacationActionNotification({
    ...data,
    status: 'accepted'
  });
};

/**
 * Wrapper for backward compatibility - calls sendVacationActionNotification
 * @param {Object|string} data - Object containing email details OR string email (legacy)
 */
export const sendVacationDeclinedEmail = (data) => {
    // If data is just a string (old usage), use dummy data for new fields
    if (typeof data === 'string') {
        return sendVacationActionNotification({
            recipients: data,
            status: 'declined',
            employeeName: 'Empleado', 
            vacationDays: 0,
            specificDates: [],
            approverEmail: 'N/A'
        });
    }

    // New usage with full object
    return sendVacationActionNotification({
        ...data,
        status: 'declined'
    });
};
